// src/components/CookieConsent.tsx
import { isString as isString2 } from "@xylabs/typeof";

// src/contexts/Context.ts
import { createContext } from "react";
var CookieConsentContext = createContext({});

// src/contexts/Loader.tsx
import { useMemo, useState } from "react";
import { jsx } from "react/jsx-runtime";
var CookiesAcceptedLocalStorageName = "CookiesAccepted";
var getAcceptedFromLocalStorage = () => {
  return localStorage.getItem(CookiesAcceptedLocalStorageName) === "true";
};
var setAcceptedToLocalStorage = (accepted) => {
  localStorage.setItem(CookiesAcceptedLocalStorageName, accepted ? "true" : "false");
};
var CookieConsentLoader = ({ children }) => {
  const [accepted, setAccepted] = useState(() => getAcceptedFromLocalStorage());
  const setAcceptedHandler = (accepted2) => {
    setAcceptedToLocalStorage(accepted2);
    setAccepted(accepted2);
  };
  const clearAccepted = () => {
    localStorage.removeItem(CookiesAcceptedLocalStorageName);
    setAccepted(getAcceptedFromLocalStorage());
  };
  const value = useMemo(() => ({
    accepted,
    clearAccepted,
    setAccepted: setAcceptedHandler,
    storageName: CookiesAcceptedLocalStorageName
  }), [accepted, clearAccepted, setAcceptedHandler]);
  return /* @__PURE__ */ jsx(CookieConsentContext, { value, children });
};

// src/contexts/use.ts
import { use } from "react";
var useCookieConsent = () => {
  return use(CookieConsentContext);
};

// src/components/CookieConsentBody.tsx
import {
  Link,
  Paper,
  Typography
} from "@mui/material";
import { ButtonEx } from "@xylabs/react-button";
import { FlexRow } from "@xylabs/react-flexbox";
import { isString } from "@xylabs/typeof";
import { useEffect } from "react";
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
var CookieConsentBody = ({
  acceptOnScroll,
  acceptOnTimer = 0,
  onAccept,
  ...props
}) => {
  const {
    accepted,
    setAccepted,
    storageName
  } = useCookieConsent();
  const onScroll = () => {
    if (window.scrollY > window.innerHeight && !accepted) {
      onAcceptClick();
    }
  };
  useEffect(() => {
    if (acceptOnScroll) {
      window.addEventListener("scroll", onScroll);
      return () => {
        window.removeEventListener("scroll", onScroll);
      };
    }
  });
  const onAcceptClick = () => {
    if (setAccepted) {
      setAccepted?.(true);
    } else {
      localStorage.setItem(storageName ?? "CookiesAccepted", "true");
    }
    onAccept?.(true);
  };
  if (acceptOnTimer > 0 && !accepted) {
    setTimeout(() => {
      onAcceptClick();
    }, acceptOnTimer);
  }
  if (!isString(storageName)) {
    return /* @__PURE__ */ jsx2(FlexRow, { justifyContent: "center", paddingY: 2, ...props, children: /* @__PURE__ */ jsx2(Typography, { color: "error", variant: "body1", children: "Missing CookieConsentContext" }) });
  }
  return accepted ? null : /* @__PURE__ */ jsxs(
    Paper,
    {
      elevation: 24,
      sx: {
        borderRadius: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        position: "fixed",
        bottom: 0,
        maxWidth: "100vw",
        width: "100%",
        zIndex: 1e3
      },
      ...props,
      children: [
        /* @__PURE__ */ jsxs(Typography, { variant: "body2", margin: 2, children: [
          "This site uses ",
          /* @__PURE__ */ jsx2(
            Link,
            {
              href: "https://cookiesandyou.com/",
              rel: "noopener noreferrer",
              target: "_blank",
              children: "cookies"
            }
          ),
          " and ",
          /* @__PURE__ */ jsx2(
            Link,
            {
              href: "https://policies.google.com/technologies/partner-sites",
              rel: "noopener noreferrer",
              target: "_blank",
              children: "Google\xA0tools"
            }
          ),
          " to analyze traffic and for ads measurement purposes."
        ] }),
        /* @__PURE__ */ jsx2(ButtonEx, { disableUserEvents: true, variant: "contained", color: "primary", onClick: onAcceptClick, margin: 2, children: "Accept" })
      ]
    }
  );
};

// src/components/CookieConsent.tsx
import { jsx as jsx3 } from "react/jsx-runtime";
var CookieConsent = (props) => {
  const { storageName } = useCookieConsent();
  const Inner = () => {
    const { accepted } = useCookieConsent();
    return accepted ? null : /* @__PURE__ */ jsx3(CookieConsentBody, { ...props });
  };
  if (!isString2(storageName)) {
    return /* @__PURE__ */ jsx3(CookieConsentLoader, { children: /* @__PURE__ */ jsx3(Inner, {}) });
  }
  return /* @__PURE__ */ jsx3(Inner, {});
};
export {
  CookieConsent,
  CookieConsentBody,
  CookieConsentContext,
  CookieConsentLoader,
  useCookieConsent
};
//# sourceMappingURL=index.mjs.map